import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  HiOutlineMagnifyingGlass,
  HiOutlineStar,
  HiStar,
  HiOutlineHome,
  HiOutlineInformationCircle,
  HiOutlineRocketLaunch,
} from "react-icons/hi2";
import { appStoreService } from "../services";
import { MAX_APPS_SEED, APP_CATEGORIES } from "../data/maxAppsDefinitions";
import { useNavLauncher } from "../hooks/useNavLauncher";
import "./AppsHub.css";

/**
 * AppsHub (The MaX App Store)
 * Shows all platform modules as rich application cards. 
 * Users can filter by category, search by name/description, and see detailed app info.
 */
function AppsHub() {
  const navigate = useNavigate();
  const { toggleFavorite, isFavorite } = useNavLauncher();
  
  // Initialize with seed data for instant "Image 1" style loading
  // This ensures the page is never blank even if the backend is offline
  const [apps, setApps] = useState(MAX_APPS_SEED);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");

  useEffect(() => {
    let isMounted = true;
    async function loadApps() {
      try {
        // We always show the seed by default (instant Image 1 style)
        const data = await appStoreService.getPublicApps();
        
        if (isMounted && data && data.length > 0) {
          // Merge API data: if an app is in API, it might have custom name/color.
          // We keep all 16 seeds, but update those that come from API.
          setApps(prev => {
            const apiMap = new Map(data.map(a => [a.code, a]));
            return MAX_APPS_SEED.map(seed => {
              const apiApp = apiMap.get(seed.code);
              return apiApp ? { ...seed, ...apiApp } : seed;
            });
          });
          setError(null);
        }
      } catch (err) {
        console.error("AppsHub: Failed to sync with backend, using seed data.", err);
        if (apps.length === 0) {
          setError("Could not load latest app data. Using local version.");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadApps();
  }, []);

  const filteredApps = useMemo(() => {
    let result = apps;
    
    // Category filter
    if (activeCategory !== "all") {
      result = result.filter(app => app.category === activeCategory);
    }

    // Search filter
    const q = query.trim().toLowerCase();
    if (q) {
      result = result.filter(app => {
        const blob = `${app.name} ${app.description} ${app.category}`.toLowerCase();
        return blob.includes(q);
      });
    }

    return result;
  }, [apps, activeCategory, query]);

  if (loading && apps.length === 0) {
    return (
      <div className="apps-hub-container">
        <header className="apps-hub-header">
          <div className="skeleton-text" style={{ width: '200px', height: '32px' }}></div>
          <div className="skeleton-text" style={{ width: '400px' }}></div>
        </header>
        <div className="apps-hub-categories">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="skeleton" style={{ width: '80px', height: '32px', borderRadius: '20px' }}></div>
          ))}
        </div>
        <div className="apps-hub-grid">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="skeleton skeleton-card" style={{ height: '320px' }}></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="apps-hub-container">
      <div className="apps-hub-intro">
        <nav className="apps-hub-breadcrumb" aria-label="Breadcrumb">
          <Link to="/home" className="apps-hub-crumb-link">
            <HiOutlineHome className="apps-hub-crumb-home-icon" aria-hidden />
            Home
          </Link>
          <span className="apps-hub-crumb-sep" aria-hidden>/</span>
          <span className="apps-hub-crumb-current">App Store</span>
        </nav>
        
        <div className="apps-hub-header">
          <div className="apps-hub-title-section">
            <h1 className="apps-hub-title">MaX Apps Store</h1>
            <p className="apps-hub-subtitle">
              Explore and launch modules designed for World Class Manufacturing.
            </p>
          </div>
          
          <div className="apps-hub-search-wrapper">
            <HiOutlineMagnifyingGlass className="apps-hub-search-icon" aria-hidden />
            <input
              type="search"
              className="apps-hub-search-input"
              placeholder="Search apps by name, feature or category..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="apps-hub-category-bar">
        {APP_CATEGORIES.map((cat) => (
          <button
            key={cat.value}
            type="button"
            className={`apps-hub-cat-pill ${activeCategory === cat.value ? "is-active" : ""}`}
            onClick={() => setActiveCategory(cat.value)}
          >
            {cat.label}
          </button>
        ))}
      </div>

      <div className="apps-hub-content">
        {loading ? (
          <div className="apps-hub-loading-grid">
            {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="apps-hub-card-skeleton" />)}
          </div>
        ) : error ? (
          <div className="apps-hub-empty">
            <HiOutlineInformationCircle style={{ fontSize: '3rem', color: 'var(--status-error)' }} />
            <h2>Offline</h2>
            <p>{error}</p>
            <button className="btn-primary" onClick={() => window.location.reload()}>
              Retry Connection
            </button>
          </div>
        ) : filteredApps.length === 0 ? (
          <div className="apps-hub-empty">
            <p>No apps found matching "{query}" in {activeCategory === 'all' ? 'any category' : activeCategory}.</p>
            <button className="btn-secondary" onClick={() => { setQuery(""); setActiveCategory("all"); }}>
              Clear all filters
            </button>
          </div>
        ) : (
          <main className="apps-hub-grid">
            {filteredApps.map((app) => (
              <AppCard 
                key={app.code} 
                app={app} 
                navigate={navigate}
                isFav={isFavorite(app.primaryPath)}
                onFavToggle={() => toggleFavorite(app.primaryPath, app.name)}
              />
            ))}
          </main>
        )}
      </div>
    </div>
  );
}

/**
 * Individual App Card Component
 */
function AppCard({ app, navigate, isFav, onFavToggle }) {
  // Use color with low opacity for background
  const bgStyle = {
    backgroundColor: `${app.colorHex || '#4F46E5'}1A`, // 1A is ~10% opacity in hex
    '--app-color': app.colorHex || '#4F46E5'
  };

  return (
    <div className="apps-hub-card" style={{ '--accent-color': app.colorHex }}>
      <button 
        className="apps-hub-card-fav"
        type="button"
        onClick={(e) => { e.stopPropagation(); onFavToggle(); }}
      >
        {isFav ? <HiStar className="fav-on" /> : <HiOutlineStar />}
      </button>

      <div className="apps-hub-card-body" onClick={() => navigate(`/home/apps/detail/${app.code}`)}>
        <div className="apps-hub-card-icon-area" style={bgStyle}>
          <span className="apps-hub-card-emoji">{app.iconEmoji || '📦'}</span>
        </div>
        
        <div className="apps-hub-card-info">
          <div className="apps-hub-card-header">
            <h3 className="apps-hub-card-name">{app.name}</h3>
            <span className="apps-hub-card-tag">{app.category}</span>
          </div>
          <p className="apps-hub-card-desc">
            {app.description?.slice(0, 85)}{app.description?.length > 85 ? "..." : ""}
          </p>
        </div>
      </div>

      <div className="apps-hub-card-footer">
        <button 
          className="apps-hub-card-btn info" 
          onClick={() => navigate(`/home/apps/detail/${app.code}`)}
        >
          <HiOutlineInformationCircle />
          Details
        </button>
        <button 
          className="apps-hub-card-btn launch" 
          onClick={() => navigate(app.primaryPath)}
        >
          <HiOutlineRocketLaunch />
          Open
        </button>
      </div>
    </div>
  );
}

export default AppsHub;
